import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import Table from "../../components/UI/CommonTable/CommonTable";
import Pagination from "../../components/UI/List/Pagination";
import ConfirmationModal from "../../components/UI/Modal/ConfirmationModal";
import useConfirmationModal from "../../hooks/useConfirmationModal";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { showToastNotification } from "../../utility/helper";
import { IProduct } from "../../interfaces/product";

const ComboProducts: React.FC = () => {
    const [products, setProducts] = useState<IProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const navigate = useNavigate();

    useEffect(() => {
        getProducts(currentPage);
    }, [currentPage]);

    const getProducts = async (page: number = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("sortType", "ASC");
            params.append("all", "true");
            params.append("isCombo", "true");

            const response = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}products/combos?${params.toString()}`,
                { method: "GET" }
            );

            const { currentPage: current, data, totalPages: total } = response;
            // Safety net in case the backend ignores the isCombo filter
            setProducts((data || []).filter((p: IProduct) => p.isCombo));
            setCurrentPage(current ?? page);
            setTotalPages(total ?? 1);
        } catch (error: any) {
            console.error("Failed to fetch combo products:", error.message);
            showToastNotification("danger", "Failed to fetch combo products");
        } finally {
            setLoading(false);
        }
    };

    const deleteProduct = async (id: string) => {
        try {
            await callApi(`${API_URL_2}${API_ENDPOINT.page}products/${id}`, { method: "DELETE" });
            showToastNotification("success", "Combo product deleted successfully");
            getProducts(currentPage);
        } catch (error: any) {
            console.error("Failed to delete combo product:", error.message);
            showToastNotification("danger", "Failed to delete combo product");
        }
    };

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: "Confirm Delete",
            message: "Are you sure you want to delete this combo product?",
            onConfirm: () => deleteProduct(id),
        });
    };

    const columns = [
        {
            header: "Image",
            accessor: "images",
            render: (images: string[]) => (
                <div className="table-image">
                    <img className="img-image" src={images && images[0]} alt="" />
                </div>
            ),
        },
        { header: "Title", accessor: "title" },
        {
            header: "Type Of Meat",
            accessor: "typeOfMeat",
            render: (types: string[]) => types?.join(", ") || "",
        },
        {
            header: "Modifier Groups",
            accessor: "modifierGroups",
            render: (mg: any[]) => mg?.length ?? 0,
        },
    ];

    const actions = [
        {
            icon: "bi bi-eye",
            color: "warning",
            onClick: (product: IProduct) => navigate(`/product-details/${product.id}`),
        },
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (product: IProduct) => navigate(`/edit-combo-product/${product.id}`),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (product: IProduct) => handleDeleteClick(product.id),
        },
    ];

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <h1 className="app-page-title mb-0">Combo Products</h1>
                <Link to="/add-combo-product" className="btn app-btn-primary d-inline-flex align-items-center gap-1 text-nowrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                    </svg>
                    Add Combo Product
                </Link>
            </div>

            <div className="app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={products}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No combo products available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default ComboProducts;
